/**
 * Voice-note waveforms: loudness sampled while recording, sent with the note
 * as whole numbers 0–100 (API `voice.waveform`, at most 64 bars). The mobile
 * app samples and draws them the same way.
 */

export const WAVEFORM_BARS = 40;

/** Notes recorded before waveforms existed: even, quiet bars — not a made-up shape. */
export const FLAT_WAVEFORM: number[] = Array.from({ length: WAVEFORM_BARS }, () => 30);

/**
 * Squeezes raw 0..1 levels into [bars] values 0–100. Each bar takes the
 * loudest sample in its slice so short syllables still show; the whole
 * shape is scaled so the loudest bar reaches the top.
 */
export const toWaveform = (levels: number[], bars = WAVEFORM_BARS): number[] => {
  if (!levels.length) return [];
  const out: number[] = [];
  for (let i = 0; i < bars; i++) {
    const from = Math.floor((i * levels.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / bars));
    let peak = 0;
    for (let j = from; j < to && j < levels.length; j++) peak = Math.max(peak, levels[j]);
    out.push(peak);
  }
  const max = Math.max(...out) || 1;
  // Floor of 6: silence still shows a sliver, like a real recording does.
  return out.map((v) => Math.max(6, Math.round((v / max) * 100)));
};

/**
 * Samples microphone loudness (RMS, 0..1) every 100ms from a live stream.
 * `levels` grows while recording (the live meter reads it); `stop` ends
 * sampling and returns the same array.
 */
export const sampleLevels = (
  stream: MediaStream,
): { levels: number[]; stop: () => number[] } => {
  const levels: number[] = [];
  let ctx: AudioContext | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  try {
    ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const data = new Float32Array(analyser.fftSize);
    timer = setInterval(() => {
      analyser.getFloatTimeDomainData(data);
      let sum = 0;
      for (const v of data) sum += v * v;
      // Speech RMS rarely passes ~0.3; stretch it so the shape has range.
      levels.push(Math.min(1, Math.sqrt(sum / data.length) * 3));
    }, 100);
  } catch {
    // No Web Audio: the note is sent without a waveform.
  }
  return {
    levels,
    stop: () => {
      if (timer) clearInterval(timer);
      void ctx?.close().catch(() => undefined);
      return levels;
    },
  };
};
